import React from 'react';
import { XCircle } from 'lucide-react';
import { formatVersionDate } from '@/utils/dateUtils';
import { versionToQuarter } from '@/utils/versionUtils';
import { buildVersionLabels } from '@/utils/versionOptions';
import {
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PlansTableHeaderProps {
  quarterlyVersions: string[];
  isScreenshotInvalid: (version: string) => boolean;
  openImageModal: (version: string, planName: string | null) => void;
}

interface PlansTableHeaderProps {
  quarterlyVersions: string[];
  isScreenshotInvalid: (version: string) => boolean;
  openImageModal: (version: string, planName: string | null) => void;
  // New optional prop for access to all scrapes
  allScrapes?: Array<{version: string, quarter: string}> | null;
}

const PlansTableHeader: React.FC<PlansTableHeaderProps> = ({
  quarterlyVersions,
  isScreenshotInvalid,
  openImageModal,
  allScrapes
}) => {
  const versionLabels = React.useMemo(() => {
    const quarters = quarterlyVersions.map(v => ({ version: v, quarter: versionToQuarter(v) }));
    return buildVersionLabels(quarters, allScrapes || []);
  }, [quarterlyVersions, allScrapes]);

  const latestVersion = versionLabels[0]?.version || null;
  return (
    <TableHeader>
      <TableRow>
        <TableHead className="w-[200px] bg-gray-100">Plan / Price</TableHead>
        <TableHead className="w-[180px] bg-gray-100">Pricing Metric</TableHead>
        <TableHead className="w-[120px] bg-gray-100">
          <div className="flex items-center space-x-1.5">
            <span>Type</span>
          </div>
        </TableHead>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <TableHead 
                className="text-center bg-gray-100 cursor-pointer hover:bg-gray-200"
                onClick={() => {
                  if (latestVersion) {
                    openImageModal(latestVersion, `All Plans - Latest`);
                  }
                }}
              >
                <div className="flex items-center justify-center gap-1">
                  Latest
                  {latestVersion && isScreenshotInvalid(latestVersion) && (
                    <XCircle className="h-3 w-3 text-red-600" />
                  )}
                </div>
              </TableHead>
            </TooltipTrigger>
            <TooltipContent>
              {latestVersion ? (
                <>
                  <p>Latest Version: {formatVersionDate(latestVersion)}</p>
                  {isScreenshotInvalid(latestVersion) && (
                    <p className="text-red-600 text-xs">Invalid screenshot</p>
                  )}
                </>
              ) : (
                <p>No versions available</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {versionLabels.slice(1).map(v => (
          <TooltipProvider key={v.version}>
            <Tooltip>
              <TooltipTrigger asChild>
                <TableHead
                  className="text-center bg-gray-100 cursor-pointer hover:bg-gray-200"
                  onClick={() => openImageModal(v.version, `All Plans - ${versionToQuarter(v.version)}`)}
                >
                  <div className="flex items-center justify-center gap-1">
                    {versionToQuarter(v.version)}
                    {isScreenshotInvalid(v.version) && (
                      <XCircle className="h-3 w-3 text-red-600" />
                    )}
                  </div>
                </TableHead>
              </TooltipTrigger>
              <TooltipContent>
                <p>Version: {formatVersionDate(v.version)}</p>
                {isScreenshotInvalid(v.version) && (
                  <p className="text-red-600 text-xs">Invalid screenshot</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
        <TableHead className="w-[60px] bg-gray-100 text-center">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );
};

export default PlansTableHeader;
